import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import StatusBadge, { PROBETRAINING_STATUS } from '../components/StatusBadge';
import Modal from '../components/Modal';
import EmailModal from '../components/EmailModal';
import { hinweis } from '../components/Hinweise';

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('de-DE') : '–'; }

const BEREICH_LABELS = { anfaenger: 'Anfänger', fortgeschritten: 'Fortgeschritten', leistung: 'Leistungsschwimmen' };

export default function Probetraining() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editBemerkung, setEditBemerkung] = useState('');
  const [editTerminDatum, setEditTerminDatum] = useState('');
  const [editTerminUhrzeit, setEditTerminUhrzeit] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [emailModal, setEmailModal] = useState(null);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (search) params.search = search;
      if (filter) params.status = filter;
      const res = await api.get('/api/admin/probetraining', { params });
      setRows(res.data.data);
      setTotal(res.data.pagination.total);
      setPages(res.data.pagination.pages || 1);
    } catch {
      setError('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [page, search, filter]);

  useEffect(() => { load(1); setPage(1); }, [search, filter]); // eslint-disable-line
  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  const openModal = (row) => {
    setSelected(row);
    setEditStatus(row.status);
    setEditBemerkung(row.bemerkung_intern || '');
    setEditTerminDatum(row.termin_datum ? String(row.termin_datum).slice(0, 10) : '');
    setEditTerminUhrzeit(row.termin_uhrzeit ? String(row.termin_uhrzeit).slice(0, 5) : '');
    setDeleteConfirm(false);
  };

  const closeModal = () => { setSelected(null); setDeleteConfirm(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/probetraining/${selected.id}`, {
        status: editStatus,
        bemerkung_intern: editBemerkung,
        termin_datum: editTerminDatum || null,
        termin_uhrzeit: editTerminUhrzeit || null,
      });
      await load(page);
      closeModal();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) { setDeleteConfirm(true); return; }
    setSaving(true);
    try {
      await api.delete(`/api/admin/probetraining/${selected.id}`);
      await load(page);
      closeModal();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Probetraining <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '1rem' }}>({total})</span></h2>
      </div>

      <div className="card">
        <div className="toolbar">
          <input type="search" className="search-input" placeholder="Suche nach Name, E-Mail…"
            value={search} onChange={e => setSearch(e.target.value)} />
          <select className="filter-select" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">Alle Status</option>
            {Object.entries(PROBETRAINING_STATUS).map(([v,c]) => <option key={v} value={v}>{c.label}</option>)}
          </select>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : rows.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">◎</div><div className="empty-state-text">Keine Anfragen gefunden</div></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>Schwimmer/in</th><th>Alter</th><th>Bereich</th><th>Termin</th><th>Status</th><th>Eingang</th></tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} onClick={() => openModal(r)}>
                    <td className="td-muted">{r.id}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.schwimmer_name}</div>
                      <div className="td-muted">{r.kontakt_name}</div>
                    </td>
                    <td>{r.schwimmer_alter ? `${r.schwimmer_alter} J.` : '–'}</td>
                    <td>{BEREICH_LABELS[r.gewuenschter_bereich] || r.gewuenschter_bereich || '–'}</td>
                    <td className="td-muted">
                      {r.termin_datum ? `${fmtDate(r.termin_datum)}${r.termin_uhrzeit ? ` ${String(r.termin_uhrzeit).slice(0,5)}` : ''}` : '–'}
                    </td>
                    <td><StatusBadge status={r.status} type="probetraining" /></td>
                    <td className="td-muted">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="pagination">
            <span className="pagination-info">{total} Einträge · Seite {page} von {pages}</span>
            <div className="pagination-btns">
              <button className="page-btn" onClick={() => setPage(p => Math.max(p-1,1))} disabled={page===1}>‹</button>
              {Array.from({length: Math.min(pages,7)},(_,i)=>i+1).map(p=>(
                <button key={p} className={`page-btn${p===page?' active':''}`} onClick={()=>setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" onClick={() => setPage(p => Math.min(p+1,pages))} disabled={page===pages}>›</button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <Modal title={`Probetraining #${selected.id} – ${selected.schwimmer_name}`} onClose={closeModal} size="lg">
          <div className="detail-section">Kontaktperson</div>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Name</div><div className="detail-value">{selected.kontakt_name}</div></div>
            <div className="detail-item"><div className="detail-label">E-Mail</div><div className="detail-value">{selected.kontakt_email}</div></div>
            <div className="detail-item"><div className="detail-label">Telefon</div><div className="detail-value">{selected.kontakt_telefon}</div></div>
          </div>

          <div className="detail-section">Schwimmer/in</div>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Name</div><div className="detail-value">{selected.schwimmer_name}</div></div>
            <div className="detail-item"><div className="detail-label">Alter</div><div className="detail-value">{selected.schwimmer_alter ? `${selected.schwimmer_alter} Jahre` : '–'}</div></div>
            <div className="detail-item"><div className="detail-label">Gewünschter Bereich</div><div className="detail-value">{BEREICH_LABELS[selected.gewuenschter_bereich] || '–'}</div></div>
            <div className="detail-item"><div className="detail-label">Wunschtermin</div><div className="detail-value">{selected.wunschtermin || '–'}</div></div>
          </div>
          {selected.schwimmerfahrung && (
            <div className="form-group">
              <label className="form-label">Schwimmerfahrung</label>
              <div style={{ background: '#f4f7fb', borderRadius: 6, padding: '0.75rem', fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{selected.schwimmerfahrung}</div>
            </div>
          )}
          {selected.nachricht && (
            <div className="form-group">
              <label className="form-label">Nachricht</label>
              <div style={{ background: '#f4f7fb', borderRadius: 6, padding: '0.75rem', fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{selected.nachricht}</div>
            </div>
          )}

          <div className="detail-section">Verwaltung</div>
          <div className="detail-grid">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={editStatus} onChange={e => setEditStatus(e.target.value)}>
                {Object.entries(PROBETRAINING_STATUS).map(([v,c]) => <option key={v} value={v}>{c.label}</option>)}
              </select>
            </div>
            <div />
            <div className="form-group">
              <label className="form-label">Termin Datum</label>
              <input type="date" className="form-control" value={editTerminDatum} onChange={e => setEditTerminDatum(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Termin Uhrzeit</label>
              <input type="time" className="form-control" value={editTerminUhrzeit} onChange={e => setEditTerminUhrzeit(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Interne Bemerkung</label>
            <textarea className="form-control" value={editBemerkung} onChange={e => setEditBemerkung(e.target.value)} rows={3} />
          </div>

          <div className="modal-actions">
            <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={saving}>{deleteConfirm ? 'Wirklich löschen?' : 'Löschen'}</button>
            {deleteConfirm && <button className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirm(false)}>Abbrechen</button>}
            {selected.kontakt_email && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => setEmailModal(selected)}>
                ✉ E-Mail
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={closeModal}>Schließen</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button>
          </div>
        </Modal>
      )}

      {emailModal && (
        <EmailModal
          an={emailModal.kontakt_email}
          anzeigeName={emailModal.kontakt_name}
          apiPfad={`/api/admin/probetraining/${emailModal.id}/email`}
          onClose={() => setEmailModal(null)}
          onSent={() => { load(page); }}
        />
      )}
    </div>
  );
}
